import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import { MaiaUploadService } from "../../data-access-layer/maia/maia-upload.service";
import { DatasetService } from "../../data-access-layer/dataset/dataset.service";
import {
    SovComputedReport,
    SovComputedSection,
    SovComputedTable,
    SovGlossaryGroup,
    SovReportDefinition,
    SovReportField,
    SovReportOverview,
    SovReportSectionDef,
    SovReportSubsectionDef,
} from "../../model/maia/sov-upload";

const FOOTPRINT_SECTION_ID = "geographical-footprint";

/** One stat tile in the File overview panel. */
interface ReportStat {
    label: string;
    value: string;
    sub?: string;
}

/**
 * MAIA - Analyze & Report ("Build your overview").
 *
 * Renders the backend-computed Overview report: a KPI header plus the configured sections' tables.
 * One SovReportDefinition is the single source of truth - the screen tailors it (report value,
 * reorder, remove, include/exclude subsections) and the same definition drives the Excel export, so
 * the workbook always matches the screen. Cosmetic edits (reorder/remove) are applied locally; edits
 * that change the numbers (report value, subsection toggle) trigger a recompute.
 */
@Component({
    selector: "maia-report-step",
    templateUrl: "./maia-report-step.component.html",
    styleUrls: ["./maia-report-step.component.scss"],
})
export class MaiaReportStepComponent implements OnInit {
    @Input() datasetId: string | null = null;
    @Input() jobId: number | null = null;

    @Output() readonly closeRequested = new EventEmitter<void>();
    @Output() readonly backRequested = new EventEmitter<void>();
    @Output() readonly continueRequested = new EventEmitter<void>();

    reportTitle = "";
    titleError = false;
    loading = false;
    computing = false;
    error = false;
    exporting = false;

    definition: SovReportDefinition | null = null;
    computed: SovComputedReport | null = null;

    // KPI header, from the computed report's overview.
    primaryLabel = "";
    primaryValue = "";
    primarySub = "";
    stats: ReportStat[] = [];

    // The job file's own columns (name, numeric, % populated) - used for the Report value and breakout pickers.
    fileFields: SovReportField[] = [];
    showMeasureEditor = false;

    // Add-content / breakout state.
    showAddContent = false;
    showBreakout = false;
    breakoutField = "";
    breakoutParentId: string | null = null;
    editingBreakoutId: string | null = null;

    // The full auto-built section set, kept so removed sections can be re-added.
    private baseSections: SovReportSectionDef[] = [];
    private computedById = new Map<string, SovComputedSection>();
    private collapsed = new Set<string>();

    constructor(
        private readonly maiaUploadService: MaiaUploadService,
        private readonly datasetService: DatasetService
    ) {}

    ngOnInit(): void {
        if (!this.datasetId || this.jobId == null) {
            return;
        }
        const datasetId = this.datasetId;
        const jobId = this.jobId;

        // The Report value and breakout pickers list the file's own columns, not taxonomy fields.
        this.maiaUploadService.getReportFields(datasetId, jobId).subscribe((fields) => {
            this.fileFields = fields || [];
            // Fields can arrive after the definition; drop a text measure now that we know field types.
            if (this.sanitizeMeasure()) {
                this.recompute();
            }
        });

        this.datasetService.getDataset(datasetId).subscribe((dataset) => {
            const base = dataset && dataset.name ? dataset.name : "Portfolio";
            this.reportTitle = `${base} Overview`;
            this.loadDefinition();
        });
    }

    private loadDefinition(): void {
        if (!this.datasetId || this.jobId == null) {
            return;
        }
        this.loading = true;
        this.error = false;
        this.maiaUploadService.getReportDefinition(this.datasetId, this.jobId, this.reportTitle).subscribe(
            (definition) => {
                // Standard sections (for the "re-add removed section" list) always come from the default;
                // the working definition is the user's saved copy (with breakouts/edits) when one exists.
                this.baseSections = JSON.parse(JSON.stringify(definition.sections));
                this.definition = this.restoreDefinition() || definition;
                this.syncFootprintRows(definition);
                this.collapsed = new Set(this.definition.sections.map((s) => s.id)); // collapsed by default
                this.collapsed.add(this.glossaryId);
                this.loading = false;
                this.sanitizeMeasure();
                this.recompute();
            },
            () => {
                this.loading = false;
                this.error = true;
            }
        );
    }

    /** A copy saved before the footprint tables changed has keys the backend no longer knows; reset its rows. */
    private syncFootprintRows(fresh: SovReportDefinition): void {
        const saved = this.definition?.sections.find((s) => this.isFootprint(s));
        const current = fresh.sections.find((s) => this.isFootprint(s));
        if (saved && current && saved.subsections.some((s) => !current.subsections.some((c) => c.key === s.key))) {
            saved.subsections = JSON.parse(JSON.stringify(current.subsections));
        }
    }

    /** Per-job localStorage key for the user's edited report definition (breakouts, toggles, order, measure). */
    private reportDefinitionKey(): string | null {
        return this.jobId == null ? null : `maia-report-definition-${this.jobId}`;
    }

    private persistDefinition(): void {
        const key = this.reportDefinitionKey();
        if (!key || !this.definition) {
            return;
        }
        try {
            localStorage.setItem(key, JSON.stringify(this.definition));
        } catch {
            // storage unavailable - edits just won't survive a reload
        }
    }

    private restoreDefinition(): SovReportDefinition | null {
        const key = this.reportDefinitionKey();
        if (!key) {
            return null;
        }
        try {
            const raw = localStorage.getItem(key);
            return raw ? (JSON.parse(raw) as SovReportDefinition) : null;
        } catch {
            return null;
        }
    }

    private recompute(): void {
        if (!this.datasetId || this.jobId == null || !this.definition) {
            return;
        }
        this.persistDefinition(); // every data-changing edit funnels through here; keep the saved copy current
        this.computing = true;
        this.maiaUploadService.computeReport(this.datasetId, this.jobId, this.definition).subscribe(
            (report) => {
                this.computed = report;
                this.computedById = new Map((report.sections || []).map((s) => [s.id, s]));
                this.openTables = new Set((report.sections || []).map((s) => this.tableKey(s.id, 0)));
                this.openFirstFootprintRow();
                this.applyOverview(report.overview);
                this.computing = false;
            },
            () => {
                this.computing = false;
                this.error = true;
            }
        );
    }

    /** The computed tables for a definition section (matched by id). */
    computedSection(id: string): SovComputedSection | undefined {
        return this.computedById.get(id);
    }

    // ---- Peril data glossary (read-only reference section, from the computed report) --------------

    readonly glossaryId = "peril-glossary";

    get glossary(): SovGlossaryGroup[] {
        return this.computed?.glossary ?? [];
    }

    get glossaryCount(): number {
        return this.glossary.reduce((n, g) => n + g.categories.length, 0);
    }

    /** Clears the title error and keeps the definition title in sync (title drives the export name). */
    onTitleInput(): void {
        if (this.reportTitle.trim()) {
            this.titleError = false;
        }
        if (this.definition) {
            this.definition.title = this.reportTitle;
        }
    }

    // ---- Report value (primary measure) ---------------------------------------------------------

    /** Only numeric columns can be the measure - a text field (e.g. MAP-Country) would sum/average to 0. */
    get measureFields(): SovReportField[] {
        return this.fileFields.filter((f) => f.numeric);
    }

    /**
     * Drops a primary measure that points at a non-numeric (text) column so the report falls back to
     * TIV (count otherwise) instead of summing text to 0. Returns true when it changed the definition.
     */
    private sanitizeMeasure(): boolean {
        if (!this.definition || !this.definition.primaryMeasure || this.fileFields.length === 0) {
            return false;
        }
        const numeric = this.fileFields.some(
            (f) => f.name === this.definition.primaryMeasure && f.numeric);
        if (numeric) {
            return false;
        }
        this.definition.primaryMeasure = null;
        return true;
    }

    selectMeasure(field: string): void {
        this.showMeasureEditor = false;
        // Empty = the "Locations (count)" option -> no measure column, count-based report.
        const measure = field || null;
        if (!this.definition || this.definition.primaryMeasure === measure) {
            return;
        }
        this.definition.primaryMeasure = measure;
        this.recompute();
    }

    // ---- Section controls (cosmetic edits stay local; data edits recompute) ---------------------

    /** Drag-reorder is cosmetic (order only) - applied locally, no recompute. */
    dropSection(event: CdkDragDrop<unknown>): void {
        if (this.definition) {
            moveItemInArray(this.definition.sections, event.previousIndex, event.currentIndex);
            this.persistDefinition();
        }
    }

    toggleCollapse(id: string): void {
        if (this.collapsed.has(id)) {
            this.collapsed.delete(id);
        } else {
            this.collapsed.add(id);
        }
    }

    isCollapsed(id: string): boolean {
        return this.collapsed.has(id);
    }

    removeSection(id: string): void {
        if (!this.definition) {
            return;
        }
        this.definition.sections = this.definition.sections.filter((s) => s.id !== id);
        this.persistDefinition();
    }

    /** Reordering footprint tables changes the Excel order, so it recomputes. */
    dropSubsection(section: SovReportSectionDef, event: CdkDragDrop<unknown>): void {
        moveItemInArray(section.subsections, event.previousIndex, event.currentIndex);
        this.recompute();
    }

    /** Toggling a subsection changes which tables appear, so it recomputes. */
    toggleSubsection(sectionId: string, key: string): void {
        if (!this.definition) {
            return;
        }
        const section = this.definition.sections.find((s) => s.id === sectionId);
        const sub = section && section.subsections.find((s) => s.key === key);
        if (!sub) {
            return;
        }
        sub.included = !sub.included;
        this.recompute();
    }

    // ---- Add content / breakouts ----------------------------------------------------------------

    /** Standard sections the file supports that the user has removed - offered for re-adding. */
    get removedSections(): SovReportSectionDef[] {
        if (!this.definition) {
            return [];
        }
        const present = new Set(this.definition.sections.map((s) => s.id));
        return this.baseSections.filter((s) => !present.has(s.id));
    }

    readdSection(id: string): void {
        if (!this.definition) {
            return;
        }
        const original = this.baseSections.find((s) => s.id === id);
        if (!original) {
            return;
        }
        this.collapsed.add(original.id);
        this.definition.sections.push(JSON.parse(JSON.stringify(original)));
        this.showAddContent = false;
        this.recompute();
    }

    /** Top-level sections only (breakouts can't be placed under another breakout). */
    get placementSections(): SovReportSectionDef[] {
        return this.definition ? this.definition.sections.filter((s) => !s.parentSectionId) : [];
    }

    isBreakout(section: SovReportSectionDef): boolean {
        return !!section.parentSectionId;
    }

    /** The field a breakout currently groups by (its single subsection's dimension). */
    breakoutDimension(section: SovReportSectionDef): string {
        return section.subsections.length ? section.subsections[0].dimension : "";
    }

    /** Inline "Group portfolio by" change on a breakout - regroups and recomputes. */
    updateBreakoutDimension(section: SovReportSectionDef, field: string): void {
        if (!field || !section.subsections.length) {
            return;
        }
        section.subsections[0].key = field;
        section.subsections[0].title = field;
        section.subsections[0].dimension = field;
        section.title = `${field} breakout`;
        this.recompute();
    }

    /** Opens the custom-breakout modal; parentId presets the "Place under" section (null = first). */
    openBreakout(parentId: string | null): void {
        this.editingBreakoutId = null;
        this.breakoutParentId = parentId
            || (this.placementSections.length ? this.placementSections[0].id : null);
        this.breakoutField = "";
        this.showAddContent = false;
        this.showBreakout = true;
    }

    /** "Move / edit" on a breakout: reopen the modal pre-filled to change its field or placement. */
    editBreakout(section: SovReportSectionDef): void {
        this.editingBreakoutId = section.id;
        this.breakoutField = this.breakoutDimension(section);
        this.breakoutParentId = section.parentSectionId;
        this.showBreakout = true;
    }

    /** Adds (or, when editing, moves/regroups) a breakout: a DIMENSION section grouping by the chosen field. */
    addBreakout(): void {
        if (!this.definition || !this.breakoutField) {
            return;
        }
        const field = this.breakoutField;
        const parentId = this.breakoutParentId;
        if (this.editingBreakoutId) {
            this.definition.sections = this.definition.sections.filter((s) => s.id !== this.editingBreakoutId);
        }
        const section: SovReportSectionDef = {
            id: this.editingBreakoutId || `breakout-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
            kind: "DIMENSION",
            title: `${field} breakout`,
            scope: "Entire portfolio",
            measure: null,
            aggregation: "sum",
            includeTable: true,
            subsections: [{ key: field, title: field, dimension: field, included: true }],
            parentSectionId: parentId,
        };
        const index = parentId ? this.definition.sections.findIndex((s) => s.id === parentId) : -1;
        if (index >= 0) {
            this.definition.sections.splice(index + 1, 0, section);
        } else {
            this.definition.sections.push(section);
        }
        this.showBreakout = false;
        this.breakoutField = "";
        this.editingBreakoutId = null;
        this.recompute();
    }

    /** "At a glance" summary line for a section, from its first computed table (prototype wording). */
    atAGlance(cs: SovComputedSection): string {
        const t = cs.tables && cs.tables[0];
        if (!t || !t.rows.length || cs.kind === "MATRIX") {
            return "";
        }
        const cols = t.columns;
        if (cs.kind === "TOP_TABLE") {
            // Last column is the external map link; the measure is the one before it.
            const measureIdx = cols.length - 2;
            if (measureIdx < 1) {
                return "";
            }
            const row = t.rows[0];
            const label = [row[1], row[2], row[3]]
                .map((v) => (v == null ? "" : String(v)).trim()).filter(Boolean).join(", ") || "Top-ranked location";
            const money = cols[measureIdx].type === "MONEY";
            const val = Number(row[measureIdx]);
            // Share of the whole portfolio, not of the top-ten subtotal.
            const total = t.portfolioTotal ?? 0;
            const valStr = money ? this.formatMoney(val) : val.toLocaleString();
            const share = total > 0 ? ` · ${this.fmtShare(val / total)} of file ${cols[measureIdx].label}` : "";
            return `${label} carries ${valStr}${share}.`;
        }
        if (cols.length < 2) {
            return "";
        }
        const money = cols[1].type === "MONEY";
        let topLabel = "";
        let topVal = -Infinity;
        for (const r of t.rows) {
            const lbl = String(r[0] == null ? "" : r[0]);
            if (lbl === "Not stated" || lbl === "All others") {
                continue;
            }
            const v = Number(r[1]);
            if (Number.isFinite(v) && v > topVal) {
                topVal = v;
                topLabel = lbl;
            }
        }
        if (!topLabel) {
            return "";
        }
        const total = t.totalRow ? Number(t.totalRow[1]) : 0;
        const share = total > 0 ? ` · ${this.fmtShare(topVal / total)} of the section total` : "";
        const locIdx = cols.findIndex((c) => c.label === "Locations");
        const located = locIdx >= 0 && t.totalRow ? Number(t.totalRow[locIdx]) : 0;
        const ofTotal = this.computed && this.computed.overview ? this.computed.overview.locations : located;
        const valStr = money ? this.formatMoney(topVal) : topVal.toLocaleString();
        return `Largest category: ${topLabel} at ${valStr}${share}. `
            + `${located.toLocaleString()} of ${ofTotal.toLocaleString()} locations are represented.`;
    }

    // ---- Subsection panels (collapsible, matching the prototype's excel-panel behaviour) ---------

    /** Expanded subsection panels, keyed "sectionId#index". First subsection of a section is open. */
    private openTables = new Set<string>();

    private tableKey(sectionId: string, index: number | string): string {
        return `${sectionId}#${index}`;
    }

    // ---- Geographical Footprint rows (one draggable, removable row per table) --------------------

    private openSubsections = new Set<string>();

    isFootprint(section: SovReportSectionDef): boolean {
        return section.id === FOOTPRINT_SECTION_ID;
    }

    subsectionTable(sectionId: string, key: string): SovComputedTable | undefined {
        return this.computedById.get(sectionId)?.tables.find((t) => t.subsectionKey === key);
    }

    /** The computed table's banner; a removed row has no table, so it mirrors the backend banner wording. */
    subsectionTitle(section: SovReportSectionDef, sub: SovReportSubsectionDef): string {
        const table = this.subsectionTable(section.id, sub.key);
        if (table?.title) {
            return table.title;
        }
        const measured = section.measure ?? this.definition?.primaryMeasure;
        return (measured ? `${measured} by ` : "Location Count by ") + sub.title;
    }

    isSubsectionOpen(sectionId: string, key: string): boolean {
        return this.openSubsections.has(this.tableKey(sectionId, key));
    }

    toggleSubsectionOpen(sectionId: string, key: string): void {
        const openKey = this.tableKey(sectionId, key);
        if (this.openSubsections.has(openKey)) {
            this.openSubsections.delete(openKey);
        } else {
            this.openSubsections.add(openKey);
        }
    }

    private openFirstFootprintRow(): void {
        const footprint = this.definition?.sections.find((s) => this.isFootprint(s));
        const first = footprint?.subsections.find((s) => s.included);
        const anyOpen = footprint?.subsections.some((s) => this.isSubsectionOpen(footprint.id, s.key));
        if (footprint && first && !anyOpen) {
            this.openSubsections.add(this.tableKey(footprint.id, first.key));
        }
    }

    /** Category sections (dimension grids, provider grids) render as collapsible subsection panels. */
    isCollapsibleSection(cs: SovComputedSection): boolean {
        return cs.kind === "PROVIDER_GRID" || cs.kind === "DIMENSION";
    }

    /** Provider grids show a "Coverage" strip, per-panel "% financial coverage", and the Excel note. */
    usesCoverage(cs: SovComputedSection): boolean {
        return cs.kind === "PROVIDER_GRID";
    }

    isTableOpen(sectionId: string, index: number): boolean {
        return this.openTables.has(this.tableKey(sectionId, index));
    }

    toggleTable(sectionId: string, index: number): void {
        const key = this.tableKey(sectionId, index);
        if (this.openTables.has(key)) {
            this.openTables.delete(key);
        } else {
            this.openTables.add(key);
        }
    }

    coveragePct(t: SovComputedTable): string {
        return t.coverage == null ? "" : this.fmtShare(t.coverage);
    }

    /** "Flood Zone: 100% · Earthquake Category: 100% · …" for the section's Coverage strip. */
    coverageStrip(cs: SovComputedSection): string {
        return cs.tables.map((t) => `${t.title}: ${this.coveragePct(t)}`).join(" · ");
    }

    // ---- Formatting -----------------------------------------------------------------------------

    /** Numeric column types are right-aligned in the table. */
    isNumericType(type: string | undefined): boolean {
        return type === "MONEY" || type === "PERCENT" || type === "COUNT" || type === "NUMBER";
    }

    isLinkType(type: string | undefined): boolean {
        return type === "LINK";
    }

    /** Formats a raw cell value by its column type (TEXT | MONEY | PERCENT | COUNT | NUMBER). */
    formatCell(value: unknown, type: string | undefined): string {
        if (value == null || value === "") {
            return "";
        }
        switch (type) {
            case "MONEY":
                return this.formatMoney(Number(value));
            case "PERCENT":
                return this.fmtShare(Number(value));
            case "COUNT":
            case "NUMBER":
                return Number(value).toLocaleString();
            default:
                return String(value);
        }
    }

    private applyOverview(o: SovReportOverview): void {
        const hasTiv = o.primaryMeasureValue != null;
        this.primaryLabel = o.primaryMeasureLabel;
        this.primaryValue = hasTiv ? this.formatHeadline(o.primaryMeasureValue as number) : o.locations.toLocaleString();
        this.primarySub = o.primaryMeasureField ? `Using the file field "${o.primaryMeasureField}"` : "File locations";

        const geoValue = o.countries > 0
            ? `${o.countries} ${o.countries === 1 ? "country" : "countries"}`
            : `${o.states} regions`;
        const geoSub = o.states > 0 ? `${o.states} states, provinces, or regions` : "";

        const concentrationLabel = o.concentrationLabel || "Largest concentration";
        const concentrationValue = o.concentrationName || "Not available";
        const concentrationSub = o.concentrationValue != null
            ? `${this.formatMoney(o.concentrationValue)}${o.concentrationShare != null
                ? ` · ${this.fmtShare(o.concentrationShare)} of file ${hasTiv ? "TIV" : "total"}`
                : ""}`
            : "Insufficient geographic data";

        this.stats = [
            { label: "Locations", value: o.locations.toLocaleString(), sub: "Included in this file" },
            { label: "Geographic scope", value: geoValue, sub: geoSub },
            { label: concentrationLabel, value: concentrationValue, sub: concentrationSub },
        ];
    }

    /** Abbreviated value, no currency symbol, matching the prototype: 101M / 26B / 540K / 1,234. */
    private abbrev(value: number): string {
        const abs = Math.abs(value);
        if (abs >= 1e9) {
            return `${(value / 1e9).toFixed(abs >= 1e10 ? 0 : 1)}`.replace(/\.0$/, "") + "B";
        }
        if (abs >= 1e6) {
            return `${(value / 1e6).toFixed(abs >= 1e7 ? 0 : 1)}`.replace(/\.0$/, "") + "M";
        }
        if (abs >= 1e3) {
            return `${(value / 1e3).toFixed(abs >= 1e4 ? 0 : 1)}`.replace(/\.0$/, "") + "K";
        }
        return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
    }

    private formatMoney(value: number): string {
        return this.abbrev(value);
    }

    /** The big KPI headline keeps two decimals (888.73M), like the prototype's summary tile. */
    private formatHeadline(value: number): string {
        const abs = Math.abs(value);
        if (abs >= 1e9) {
            return `${(value / 1e9).toFixed(2)}B`;
        }
        if (abs >= 1e6) {
            return `${(value / 1e6).toFixed(2)}M`;
        }
        if (abs >= 1e3) {
            return `${(value / 1e3).toFixed(2)}K`;
        }
        return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
    }

    /** Percent share like the prototype: whole number at >=10%, else one decimal. */
    private fmtShare(fraction: number): string {
        const p = fraction * 100;
        return (p >= 10 ? p.toFixed(0) : p.toFixed(1)) + "%";
    }

    // ---- Actions --------------------------------------------------------------------------------

    generateOverview(): void {
        if (!this.reportTitle.trim()) {
            this.titleError = true;
            return;
        }
        if (!this.datasetId || this.jobId == null || !this.definition) {
            return;
        }
        this.definition.title = this.reportTitle;
        this.exporting = true;
        const name = `${this.reportTitle.trim()}.xlsx`;
        this.maiaUploadService.exportOverview(this.datasetId, this.jobId, this.definition).subscribe(
            (blob) => {
                this.saveBlob(blob, name);
                this.exporting = false;
            },
            (err) => {
                this.exporting = false;
                if (err && err.error instanceof Blob) {
                    err.error.text().then((text: string) => console.error("Overview export failed", err.status, text));
                } else {
                    console.error("Overview export failed", err);
                }
            }
        );
    }

    downloadEnriched(): void {
        if (this.jobId == null) {
            return;
        }
        const name = `${(this.reportTitle || "Portfolio").trim()} Enriched.xlsx`;
        this.maiaUploadService.downloadEnriched(this.jobId).subscribe(
            (blob) => this.saveBlob(blob, name),
            (error) => console.error("Enriched download failed", error)
        );
    }

    private saveBlob(blob: Blob, name: string): void {
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = name;
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    }
}
